/**
 * スキル有効性チェッカー
 *
 * AIがスキルを発動する前に、そのスキルが実際に効果を発揮できるか
 * （条件を満たしているか）を事前チェックするシステム
 *
 * 問題背景:
 * - 輝聖女ルミナスの上級技「手札から光属性モンスターを召喚」は、
 *   手札に光属性モンスターがないと効果なしでreturn falseする
 * - この場合 usedSkillThisTurn が設定されないため、
 *   AIが同じ技を繰り返し選択して無限ループになる
 *
 * 解決策:
 * - AIがスキルを選択する前に「効果が有効か」をチェック
 * - 有効でないスキルは使用可能リストから除外
 */

import type { MonsterInstance } from '../../types/card';
import type { AIGameState, UsableSkill } from './strategies/base';

/** スキル有効性チェッカー関数型 */
type SkillChecker = (monster: MonsterInstance, gameState: AIGameState) => boolean;

/** カードIDごとのスキルチェッカー型 */
interface CardSkillCheckers {
  basic?: SkillChecker;
  advanced?: SkillChecker;
}

/**
 * カードIDとスキルタイプごとの有効性チェッカー
 * チェッカーが未定義のスキルはデフォルトで有効とみなす
 */
const skillEffectivityCheckers: Record<string, CardSkillCheckers> = {
  /**
   * C0000056: 輝聖女ルミナス
   * 上級技: 手札から光属性モンスター1体をコストなしで召喚可能
   * → 手札に光属性モンスターが必要、フィールドに空きが必要
   */
  C0000056: {
    advanced: (monster, gameState) => {
      // 手札に光属性モンスターがあるか
      const lightMonsters = gameState.myHand.filter(
        card => card.type === 'monster' && card.attribute === '光'
      );
      if (lightMonsters.length === 0) {
        return false;
      }

      // フィールドに空きがあるか
      const emptySlot = gameState.myField.findIndex(slot => slot === null);
      if (emptySlot === -1) {
        return false;
      }

      return true;
    },
  },

  /**
   * C0000142: ブリザードマスター
   * 上級技: デッキから「ブリザードキャット」と名のつくモンスターを1体特殊召喚
   * → フィールドに空きが必要（デッキの中身はAIが確認できないため、空きだけチェック）
   */
  C0000142: {
    advanced: (monster, gameState) => {
      const emptySlot = gameState.myField.findIndex(slot => slot === null);
      return emptySlot !== -1;
    },
  },

  /**
   * C0000044: 水晶のマーメイド
   * 基本技: 手札の水属性モンスター1体のコストを1下げる
   * → 手札に水属性モンスターが必要
   */
  C0000044: {
    basic: (monster, gameState) => {
      const waterMonsters = gameState.myHand.filter(
        card => card.type === 'monster' && card.attribute === '水'
      );
      return waterMonsters.length > 0;
    },
  },

  /**
   * C0000028: 炎竜母フレイマ
   * 基本技: 場の［ドラゴン］モンスター1体のHPを500回復する
   * → 場にドラゴン族モンスターが必要
   * 上級技: 墓地からドラゴン族モンスター1体を特殊召喚（攻撃力半減）
   * → 墓地にドラゴン族モンスターが必要、フィールドに空きが必要
   */
  C0000028: {
    basic: (monster, gameState) => {
      // 場にドラゴンがいるか
      const dragonsOnField = gameState.myField.filter(
        m => m && (m.category?.includes('ドラゴン') ||
                   (Array.isArray(m.category) && m.category.some(c => c.includes('ドラゴン'))))
      );
      return dragonsOnField.length > 0;
    },
    advanced: (monster, gameState) => {
      // 墓地にドラゴンがあるか
      const dragons = gameState.myGraveyard.filter(
        card => card.type === 'monster' &&
                (card.category?.includes('ドラゴン') ||
                 (Array.isArray(card.category) && card.category.some(c => c.includes('ドラゴン'))))
      );
      if (dragons.length === 0) {
        return false;
      }

      // フィールドに空きがあるか
      const emptySlot = gameState.myField.findIndex(slot => slot === null);
      return emptySlot !== -1;
    },
  },

  /**
   * C0000271: 黄金の時姫エクラリア
   * 上級技: 勝負中1度のみ、デッキ上3枚から1枚を召喚（コスト無視）
   * → 勝負中1度のみ（usedUltimateSkillC0000271フラグ）
   * → フィールドに空きが必要（自分以外のスロット）
   * → デッキにカードが必要
   */
  C0000271: {
    advanced: (monster, gameState) => {
      // 勝負中1度のみチェック
      if (monster.usedUltimateSkillC0000271) {
        return false;
      }

      // デッキにカードがあるか
      if (!gameState.myDeck || gameState.myDeck.length === 0) {
        return false;
      }

      // フィールドに空きがあるか（自分のスロット以外）
      const monsterIndex = gameState.myField.findIndex(m => m && m.uniqueId === monster.uniqueId);
      const emptySlot = gameState.myField.findIndex((slot, idx) => slot === null && idx !== monsterIndex);
      return emptySlot !== -1;
    },
  },

  /**
   * C0000258: 禁忌の鴉王ミラン
   * 上級技: 勝負中1度のみ、相手デッキ上2枚を「カラス人形」に変換して相手の場に召喚
   * → 勝負中1度のみ（usedUltimateSkillC0000258フラグ）
   * → 相手デッキにカードが必要
   * → 相手フィールドに空きが必要
   */
  C0000258: {
    advanced: (monster, gameState) => {
      // 勝負中1度のみチェック
      if (monster.usedUltimateSkillC0000258) {
        return false;
      }

      // 相手デッキにカードがあるか
      if (!gameState.opponentDeck || gameState.opponentDeck.length === 0) {
        return false;
      }

      // 相手フィールドに空きがあるか
      const emptySlot = gameState.opponentField.findIndex(slot => slot === null);
      return emptySlot !== -1;
    },
  },

  /**
   * C0000272: 虚蝕の崩姫エクラリア【禁忌カード】
   * 上級技: 勝負中1度のみ、墓地の未来属性3枚をデッキに戻し、相手全体に3000ダメージ
   * → 勝負中1度のみ（usedUltimateSkillC0000272フラグ）
   * → 墓地に未来属性カードが3枚以上必要
   */
  C0000272: {
    advanced: (monster, gameState) => {
      // 勝負中1度のみチェック
      if (monster.usedUltimateSkillC0000272) {
        return false;
      }

      // 墓地に未来属性カードが3枚以上あるか
      const futureCards = (gameState.myGraveyard || []).filter(
        card => card.attribute === '未来'
      );
      return futureCards.length >= 3;
    },
  },

  /**
   * C0000049: タイダルシフト（魔法カードだが参考用）
   * 手札とフィールドのモンスターを入れ替え
   * → 手札にモンスターが必要、フィールドにモンスターが必要
   */

  /**
   * ソラリア系のカード: 手札から特定属性を召喚する系
   * 必要に応じて追加
   */

  /**
   * C0000043: 深海のクラーケン
   * 基本技: コスト4以下の相手モンスター1体を凍結状態にする
   * → 相手の場にコスト4以下の凍結でないモンスターが必要
   */
  C0000043: {
    basic: (monster, gameState) => {
      // 相手の場にコスト4以下で凍結でないモンスターがいるか
      const validTargets = gameState.opponentField.filter((m) => {
        if (!m) return false;
        if (m.cost > 4) return false;
        // 凍結状態かチェック
        const isFrozen =
          m.statusEffects &&
          m.statusEffects.some((effect) => effect.type === 'FREEZE');
        return !isFrozen;
      });
      return validTargets.length > 0;
    },
  },

  /**
   * C0000425: 灰骨竜フレアスケルス
   * 基本技: SPトークン1個をレスト状態にし、相手フィールド全体に1000ダメージ
   * → アクティブSPが1以上必要、相手フィールドにモンスターが必要
   */
  C0000425: {
    basic: (monster, gameState) => {
      // アクティブSPが1以上あるか
      if (gameState.myActiveSP < 1) {
        return false;
      }
      // 相手フィールドにモンスターがいるか
      const hasOpponentMonster = gameState.opponentField.some(m => m !== null);
      return hasOpponentMonster;
    },
  },

  /**
   * C0000347: 鳥民の供物者・カルディス
   * 基本技: このカードをリリースし、次の《ヴォランティス》モンスターの召喚コストを2軽減
   * → 手札に「ヴォランティス」と名のつくモンスターが必要
   */
  C0000347: {
    basic: (monster, gameState) => {
      // 手札に「ヴォランティス」と名のつくモンスターがいるか
      const voluntisInHand = gameState.myHand.filter(
        card => card.type === 'monster' && card.name && card.name.includes('ヴォランティス')
      );
      return voluntisInHand.length > 0;
    },
  },

  /**
   * C0000369: 炎翼の鳥民・イグニス
   * 基本技: このカードを破壊し、自分の《ヴォランティス》モンスター1体の攻撃力+1000
   * → 自分の場に「ヴォランティス」と名のつくモンスターが必要
   */
  C0000369: {
    basic: (monster, gameState) => {
      // 自分の場に「ヴォランティス」と名のつくモンスターがいるか
      const voluntisMonsters = gameState.myField.filter(
        m => m && m.name && m.name.includes('ヴォランティス')
      );
      return voluntisMonsters.length > 0;
    },
  },

  /**
   * C0000368: 裁定の鳥民・ミナルス
   * 基本技: このカードをリリースし、墓地の《ヴォランティス》モンスター1体を手札に戻す
   * → 墓地に「ヴォランティス」がいて、かつそのコストが現在SPより低い（召喚可能）
   */
  C0000368: {
    basic: (monster, gameState) => {
      // 墓地に「ヴォランティス」と名のつくモンスターがいるか
      const voluntisInGraveyard = (gameState.myGraveyard || []).filter(
        card => card.type === 'monster' && card.name && card.name.includes('ヴォランティス')
      );
      if (voluntisInGraveyard.length === 0) {
        return false;
      }
      // そのうち現在SPで召喚可能なものがあるか
      const currentSP = gameState.myActiveSP || 0;
      const summonableVolantis = voluntisInGraveyard.some(card => card.cost < currentSP);
      return summonableVolantis;
    },
  },

  /**
   * C0000356: 鳥民の斥候・ヴェルティス
   * 基本技: このカードをリリースし、自分の《ヴォランティス》モンスターが受ける効果ダメージを0にする
   * → 自分の場に「ヴォランティス」と名のつくモンスターが必要（自分以外）
   */
  C0000356: {
    basic: (monster, gameState) => {
      // 自分の場に「ヴォランティス」と名のつくモンスターがいるか（自分以外）
      const voluntisMonsters = gameState.myField.filter(
        m => m && m.name && m.name.includes('ヴォランティス') && m.uniqueId !== monster.uniqueId
      );
      return voluntisMonsters.length > 0;
    },
  },

  /**
   * C0000396: 呪骸王エリザルヴェドン
   * 基本技: 自分の墓地の《黒呪》魔法カード1枚をデッキに戻し、攻撃力+800（ターンに1度）
   * → 墓地に「黒呪」魔法カードが必要
   * 上級技: 自分の墓地の《黒呪》魔法カード1枚をデッキに戻し、そのカードの効果を発動する
   * → 墓地に「黒呪」魔法カードが必要
   */
  C0000396: {
    basic: (monster, gameState) => {
      // 墓地に「黒呪」魔法カードがあるか
      const kurojuMagics = (gameState.myGraveyard || []).filter(
        card => card.type === 'magic' && card.name && card.name.includes('黒呪')
      );
      return kurojuMagics.length > 0;
    },
    advanced: (monster, gameState) => {
      // 墓地に「黒呪」魔法カードがあるか
      const kurojuMagics = (gameState.myGraveyard || []).filter(
        card => card.type === 'magic' && card.name && card.name.includes('黒呪')
      );
      return kurojuMagics.length > 0;
    },
  },

  /**
   * C0000318: 雷鳴の使者
   * 基本技: 自身の攻撃力を500下げて、500ダメージを相手モンスター1体に与える
   * → 相手フィールドにモンスターが必要
   */
  C0000318: {
    basic: (monster, gameState) => {
      // 相手フィールドにモンスターがいるか
      const hasOpponentMonster = gameState.opponentField.some(m => m !== null);
      return hasOpponentMonster;
    },
  },

  /**
   * C0000023: レッドバーストドラゴン
   * 基本技: 自分の手札を1枚捨てて、このカードの攻撃力をターン終了時まで1000アップ
   * → 手札が1枚以上必要
   */
  C0000023: {
    basic: (monster, gameState) => {
      // 手札が1枚以上あるか
      return gameState.myHand && gameState.myHand.length > 0;
    },
  },

  /**
   * C0000403: 沼の血叫びヴォルガノス
   * 基本技: SPトークン1個をレスト状態にし、相手モンスター1体の攻撃力を1000ダウン
   * → SPが1以上必要、相手フィールドにモンスターが必要
   * 上級技: SPトークン1個をレスト状態にし、1枚ドロー
   * → SPが1以上必要、デッキにカードが必要
   */
  C0000403: {
    basic: (monster, gameState) => {
      // SPが1以上あるか
      if (gameState.myActiveSP < 1) {
        return false;
      }
      // 相手フィールドにモンスターがいるか
      const hasOpponentMonster = gameState.opponentField.some(m => m !== null);
      return hasOpponentMonster;
    },
    advanced: (monster, gameState) => {
      // SPが1以上あるか
      if (gameState.myActiveSP < 1) {
        return false;
      }
      // デッキにカードがあるか
      if (!gameState.myDeck || gameState.myDeck.length === 0) {
        return false;
      }
      return true;
    },
  },

  /**
   * C0000354: 鳥民の風使い・ゼルヴィス
   * 基本技: 自分のモンスター1体のHPを800回復。場に《ヴォランティス》モンスターがいる場合、このカードの攻撃力をターン終了時まで800アップ。
   * → 自分のフィールドにモンスターが必要（HP回復対象として）
   */
  C0000354: {
    basic: (monster, gameState) => {
      // 自分のフィールドにモンスターがいるか（自分自身も含む）
      const hasMonster = gameState.myField.some(m => m !== null);
      return hasMonster;
    },
  },

  /**
   * C0000564: 黒霧の呪術師
   * 基本技: 相手モンスター1体を【彷徨い】状態にする。このターン、このカードは攻撃できない。
   * → 相手フィールドにモンスターが必要（飛行持ちは耐性あるので除外）
   */
  C0000564: {
    basic: (monster, gameState) => {
      // 相手フィールドにモンスターがいるか（飛行持ちは彷徨い耐性あり）
      const targetableMonsters = gameState.opponentField.filter(m => {
        if (!m) return false;
        // 飛行キーワードを持つモンスターは耐性があるので除外
        if (m.keyword && (
          (typeof m.keyword === 'string' && m.keyword.includes('【飛行】')) ||
          (Array.isArray(m.keyword) && m.keyword.some(k => k.includes('【飛行】')))
        )) {
          return false;
        }
        return true;
      });
      return targetableMonsters.length > 0;
    },
  },

  /**
   * C0000565: 虚ろの収集家
   * 基本技: 相手モンスター1体を【彷徨い】状態にする。
   * → 相手フィールドに飛行持ち以外のモンスターが必要
   */
  C0000565: {
    basic: (monster, gameState) => {
      const targetableMonsters = gameState.opponentField.filter(m => {
        if (!m) return false;
        if (m.keyword && (
          (typeof m.keyword === 'string' && m.keyword.includes('【飛行】')) ||
          (Array.isArray(m.keyword) && m.keyword.some(k => k.includes('【飛行】')))
        )) {
          return false;
        }
        return true;
      });
      return targetableMonsters.length > 0;
    },
  },

  /**
   * C0000566: 彷徨いの王・ワンダラー
   * 基本技: 相手の【彷徨い】状態のモンスターの数×1枚ドローする。
   * → 相手フィールドに彷徨い状態のモンスターが必要
   */
  C0000566: {
    basic: (monster, gameState) => {
      const wanderingMonsters = gameState.opponentField.filter(m => {
        if (!m) return false;
        if (!m.statusEffects) return false;
        return (m.statusEffects as Array<{ type: string }>).some(
          s => s.type === 'WANDERING'
        );
      });
      return wanderingMonsters.length > 0;
    },
  },

  /**
   * C0000572: 目覚めの歌い手
   * 基本技: 味方モンスター1体の状態異常をすべて解除する
   * → 自分のフィールドに状態異常を持つモンスターが必要
   */
  C0000572: {
    basic: (monster, gameState) => {
      const monstersWithStatus = gameState.myField.filter(m => {
        if (!m) return false;
        if (!m.statusEffects) return false;
        return (m.statusEffects as Array<{ type: string }>).length > 0;
      });
      return monstersWithStatus.length > 0;
    },
  },

  /**
   * C0000573: 魂呼びの聖火巫女
   * 基本技: 「聖火の救済」を1枚手札に加える
   * → 常に有効（カード生成は手札制限なしで可能）
   */
  C0000573: {
    basic: (_monster, _gameState) => {
      // カード生成は常に有効
      return true;
    },
  },

  /**
   * C0000561: カーテン職人デンガク
   * 基本技: 「遮光カーテン」を1枚手札に加える
   * → 常に有効（カード生成は手札制限なしで可能）
   */
  C0000561: {
    basic: (_monster, _gameState) => {
      // カード生成は常に有効
      return true;
    },
  },

  /**
   * C0000419: 毒蛾モルフィグナ
   * 基本技: SPトークン1個をレスト状態にし、受けるダメージを300軽減
   * → アクティブSPが1以上必要
   */
  C0000419: {
    basic: (_monster, gameState) => {
      return gameState.myActiveSP >= 1;
    },
  },
};

/**
 * スキルが有効かどうかをチェック
 */
export function isSkillEffective(
  monster: MonsterInstance | null | undefined,
  skillType: 'basic' | 'advanced',
  gameState: AIGameState
): boolean {
  if (!monster || !monster.id) {
    return true; // チェックできない場合はデフォルトで有効
  }

  const checker = skillEffectivityCheckers[monster.id];
  if (!checker) {
    return true; // チェッカーが未定義の場合はデフォルトで有効
  }

  const skillChecker = checker[skillType];
  if (!skillChecker) {
    return true; // そのスキルタイプのチェッカーがない場合はデフォルトで有効
  }

  try {
    return skillChecker(monster, gameState);
  } catch (error) {
    console.warn(`スキル有効性チェックでエラー: ${monster.name} ${skillType}`, error);
    return true; // エラー時はデフォルトで有効
  }
}

/**
 * 使用可能なスキルリストから無効なスキルを除外
 */
export function filterEffectiveSkills(
  usableSkills: UsableSkill[],
  gameState: AIGameState
): UsableSkill[] {
  return usableSkills.filter(skill => {
    return isSkillEffective(skill.monster, skill.skillType, gameState);
  });
}

export default {
  isSkillEffective,
  filterEffectiveSkills,
  // 外部からチェッカーを追加可能にする
  addChecker: (cardId: string, checkers: CardSkillCheckers) => {
    skillEffectivityCheckers[cardId] = {
      ...skillEffectivityCheckers[cardId],
      ...checkers,
    };
  },
};
